import { Controller } from "@hotwired/stimulus"
import { triggerHaptic } from "../src/trigger_native_haptic"

// Plays the routine check out the first time someone checks a product against
// their routine, the way an assistant shows its work. The checks open above
// the verdict and run one at a time: each line appears saying what it's doing,
// shimmering, then turns into what came back, and a flagged result holds a
// beat longer than a clean one. The verdict then fades in word by word right
// below and the buttons come in with it. Once every check has come back,
// the checks then fold up under the scope line once the verdict has had a
// moment to be read, carrying the verdict up with them; a flag takes over the
// scope line as they go, so it stays in view. The usage guide rises in last. On native, each result taps a light haptic and
// the verdict a firmer one.
//
// The result is computed on the server, so this only paces the reveal of a
// finished check, kept slow enough to read along, and a tap skips to the end.
//
// The markup ships in its final state, checks folded, so the check reads
// without JS. This controller primes the checking state itself, and leaves
// the final state alone under prefers-reduced-motion or once this product has
// been checked against this routine (keyValue changes when the routine does).
const START_MS = 400
const RUN_MS = 800
const FLAGGED_RUN_MS = 1200
const STEP_GAP_MS = 300
const SUBTITLE_DELAY_MS = 150
const WORD_MS = 50
const FOLD_DELAY_MS = 900
const FOLD_MS = 550
const OPEN_EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)"
// A running check's dot, warm-gray-400.
const RUNNING_DOT = "#a8a29e"
// The footer carries a single and a double native bar and switches on the one
// that fits the plan, so holding back sets each one's disabled value.
const NATIVE_DISABLED_ATTRIBUTES = ["data-bridge--single-action-disabled-value", "data-bridge--double-action-disabled-value"]

export default class extends Controller {
    static targets = ["eyebrow", "checkingEyebrow", "checkedEyebrow", "trace", "row", "lede", "guide", "action", "nativeAction"]
    static values = { key: String }

    connect() {
        this.timers = []
        this.playing = false
        if (!this.hasTraceTarget) return

        // A Turbo snapshot can catch the check mid-run, so a restored page
        // always settles on the final state before deciding anything else.
        this.instantly(() => this.finish())
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || this.seen) return

        this.remember()
        this.instantly(() => this.prime())
        this.play()
    }

    disconnect() {
        this.stop()
    }

    skip() {
        if (!this.playing) return

        this.stop()
        this.finish()
    }

    prime() {
        this.eyebrowTarget.hidden = true
        this.checkingEyebrowTarget.hidden = false
        this.traceTarget.style.gridTemplateRows = "1fr"
        this.rowTargets.forEach(row => {
            row.style.opacity = "0"
            row.style.transform = "translateY(4px)"
            this.showRunning(row)
            this.rail(row).style.transform = "scaleY(0)"
        })
        this.words.forEach(word => {
            word.style.opacity = "0"
            word.style.filter = "blur(3px)"
        })
        this.guideTargets.forEach(guide => {
            guide.style.opacity = "0"
            guide.style.transform = "translateY(10px)"
            guide.style.pointerEvents = "none"
        })
        this.actionTargets.forEach(action => {
            action.style.opacity = "0"
            action.style.transform = "translateY(8px)"
            action.style.pointerEvents = "none"
        })
        this.nativeActionTargets.forEach(action => {
            NATIVE_DISABLED_ATTRIBUTES.forEach(attribute => action.setAttribute(attribute, "true"))
        })
    }

    play() {
        this.playing = true

        let at = START_MS
        this.rowTargets.forEach(row => {
            const run = row.hasAttribute("data-reveal-flagged") ? FLAGGED_RUN_MS : RUN_MS
            this.later(at, () => {
                row.style.transition = `opacity 300ms ease-out, transform 350ms ${OPEN_EASE}`
                row.style.opacity = ""
                row.style.transform = ""
            })
            this.later(at + run, () => this.land(row))
            at += run + STEP_GAP_MS
        })

        // A flag's scope line waits for the fold, so the plain one stands in.
        const scope = this.hasCheckedEyebrowTarget ? this.checkedEyebrowTarget : this.eyebrowTarget
        this.later(at, () => {
            scope.hidden = false
            this.checkingEyebrowTarget.hidden = true
            triggerHaptic()
        })
        this.words.forEach((word, index) => {
            this.later(at + index * WORD_MS, () => {
                word.style.transition = "opacity 450ms ease-out, filter 450ms ease-out"
                word.style.opacity = ""
                word.style.filter = ""
            })
        })

        // The answer is in, so the buttons can be used while it's read.
        const read = at + this.words.length * WORD_MS + 300
        this.later(read, () => {
            this.actionTargets.forEach(action => {
                action.style.transition = `opacity 350ms ease-out, transform 350ms ${OPEN_EASE}`
                action.style.opacity = ""
                action.style.transform = ""
                action.style.pointerEvents = ""
            })
            this.releaseNativeActions()
        })

        // Clear checks fold once the verdict has had a moment, which carries
        // the verdict up under the scope line, and the guide waits for that.
        const folds = this.traceTarget.hasAttribute("data-reveal-fold")
        const fold = read + (folds ? FOLD_DELAY_MS : 0)
        if (folds) this.later(fold, () => {
            this.traceTarget.style.gridTemplateRows = ""
            if (scope !== this.eyebrowTarget) this.flagScope()
        })
        this.later(fold + 300, () => {
            this.guideTargets.forEach(guide => {
                guide.style.opacity = ""
                guide.style.transform = ""
                guide.style.pointerEvents = ""
            })
        })
        this.later(fold + FOLD_MS + 400, () => {
            this.playing = false
            this.finish()
        })
    }

    // The shimmering line crossfades into the result in the same spot, its
    // gray line follows, the dot eases to its color and the rail draws on to
    // the next check.
    land(row) {
        const [result, ...rest] = row.querySelectorAll("[data-reveal-result]")
        row.querySelector("[data-reveal-progress]").style.opacity = "0"
        result.style.opacity = ""
        this.later(SUBTITLE_DELAY_MS, () => rest.forEach(line => { line.style.opacity = "" }))
        const dot = row.querySelector("[data-reveal-dot]")
        dot.style.backgroundColor = ""
        dot.animate([{ transform: "scale(0.7)" }, { transform: "scale(1)" }], { duration: 300, easing: "ease-out" })
        const rail = this.rail(row)
        rail.style.transition = `transform ${STEP_GAP_MS + 100}ms ease-in-out`
        rail.style.transform = ""
        triggerHaptic("light")
    }

    finish() {
        this.eyebrowTarget.hidden = false
        this.checkingEyebrowTarget.hidden = true
        if (this.hasCheckedEyebrowTarget) this.checkedEyebrowTarget.hidden = true
        this.traceTarget.style.gridTemplateRows = ""
        this.rowTargets.forEach(row => {
            row.style.opacity = ""
            row.style.transform = ""
            row.style.transition = ""
            this.showResult(row)
            const rail = this.rail(row)
            rail.style.transform = ""
            rail.style.transition = ""
        })
        this.words.forEach(word => {
            word.style.opacity = ""
            word.style.filter = ""
            word.style.transition = ""
        })
        this.guideTargets.forEach(guide => {
            guide.style.opacity = ""
            guide.style.transform = ""
            guide.style.pointerEvents = ""
        })
        this.actionTargets.forEach(action => {
            action.style.opacity = ""
            action.style.transform = ""
            action.style.transition = ""
            action.style.pointerEvents = ""
        })
        this.releaseNativeActions()
    }

    // The flag rises into the scope line as its row folds away under it.
    flagScope() {
        this.checkedEyebrowTarget.hidden = true
        this.eyebrowTarget.hidden = false
        this.eyebrowTarget.animate(
            [{ opacity: 0, transform: "translateY(4px)" }, { opacity: 1, transform: "none" }],
            { duration: 350, easing: OPEN_EASE }
        )
    }

    stop() {
        this.timers.forEach(clearTimeout)
        this.timers = []
        this.playing = false
    }

    releaseNativeActions() {
        this.nativeActionTargets.forEach(action => {
            NATIVE_DISABLED_ATTRIBUTES.forEach(attribute => action.removeAttribute(attribute))
        })
    }

    // A running check shows what it's doing where its result will be, the
    // result and its gray line held back and its dot gray. Everything stays
    // in the layout, so landing only crossfades.
    showRunning(row) {
        const progress = row.querySelector("[data-reveal-progress]")
        progress.hidden = false
        progress.style.opacity = ""
        row.querySelectorAll("[data-reveal-result]").forEach(result => { result.style.opacity = "0" })
        row.querySelector("[data-reveal-dot]").style.backgroundColor = RUNNING_DOT
    }

    showResult(row) {
        const progress = row.querySelector("[data-reveal-progress]")
        progress.hidden = true
        progress.style.opacity = ""
        row.querySelectorAll("[data-reveal-result]").forEach(result => { result.style.opacity = "" })
        row.querySelector("[data-reveal-dot]").style.backgroundColor = ""
    }

    rail(row) {
        return row.querySelector("[data-reveal-rail]")
    }

    // The verdict's words, each in its own span so they can fade in one by
    // one. Split once; the spans read the same as the plain text.
    get words() {
        if (!this.ledeTarget.hasAttribute("data-reveal-split")) {
            this.ledeTarget.querySelectorAll(":scope > span").forEach(part => {
                const text = part.textContent.trim()
                part.textContent = ""
                text.split(/\s+/).forEach((word, index) => {
                    if (index > 0) part.append(" ")
                    const span = document.createElement("span")
                    span.textContent = word
                    part.append(span)
                })
            })
            this.ledeTarget.setAttribute("data-reveal-split", "")
        }
        return Array.from(this.ledeTarget.querySelectorAll(":scope > span > span"))
    }

    // Switches states without animating, so priming doesn't visibly fade the
    // final state out and a restored snapshot settles straight away.
    instantly(callback) {
        const elements = [this.element, ...this.element.querySelectorAll("*")]
        elements.forEach(element => { element.style.transition = "none" })
        callback()
        void this.element.offsetHeight
        elements.forEach(element => { element.style.transition = "" })
    }

    later(delay, callback) {
        this.timers.push(setTimeout(callback, delay))
    }

    get seen() {
        try {
            return window.localStorage.getItem(this.keyValue) !== null
        } catch {
            return false
        }
    }

    remember() {
        try {
            window.localStorage.setItem(this.keyValue, "1")
        } catch {
            // Storage can be unavailable (private mode); the check just replays.
        }
    }
}
